import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Scan, 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  CreditCard, 
  DollarSign,
  X,
  Search,
  User,
  CheckCircle,
  Save,
  Clock,
  ChevronRight,
  ChevronLeft,
  FileText,
  Minimize,
  Home,
  Tag,
  Maximize,
  Minimize2,
  Wallet,
  Landmark,
  Banknote,
  Receipt,
  ArrowRightLeft,
  Loader2
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const POS = ({ user }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [ncfTypes, setNcfTypes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formasPago, setFormasPago] = useState([]);
  const [montoRecibido, setMontoRecibido] = useState('');
  const [cambio, setCambio] = useState(0);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [rncLookupLoading, setRncLookupLoading] = useState(false);
  const [rncPreview, setRncPreview] = useState(null);
  
  // Estados para Facturas Abiertas
  const [openInvoices, setOpenInvoices] = useState([]);
  const [showOpenInvoices, setShowOpenInvoices] = useState(true);
  const [currentOpenInvoiceId, setCurrentOpenInvoiceId] = useState(null);
  const [currentOpenInvoiceName, setCurrentOpenInvoiceName] = useState('');
  const [saveOpenDialogOpen, setSaveOpenDialogOpen] = useState(false);
  const [openInvoiceName, setOpenInvoiceName] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  
  // Estados para Ofertas
  const [ofertasActivas, setOfertasActivas] = useState([]);
  const [ofertasAplicadas, setOfertasAplicadas] = useState([]);
  
  // Estado para Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  
  // Estados para Créditos a Favor (Notas de Crédito)
  const [creditosDisponibles, setCreditosDisponibles] = useState([]);
  const [totalCreditoDisponible, setTotalCreditoDisponible] = useState(0);
  const [creditosAplicados, setCreditosAplicados] = useState([]);
  const [creditoDialogOpen, setCreditoDialogOpen] = useState(false);
  
  const barcodeInputRef = useRef(null);

  const [paymentData, setPaymentData] = useState({
    tipo_ncf: 'B02',
    cliente_id: '',
    cliente_nombre: '',
    cliente_rnc: '',
    cliente_telefono: '',
    cliente_direccion: '',
    tipo_pago: 'efectivo',
    es_credito: false,
    dias_credito: 30,
  });

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchCategories();
    fetchBusinessConfig();
    fetchNCFTypes();
    fetchOpenInvoices();
    fetchOfertasActivas();
    
    // Intentar activar fullscreen automáticamente al entrar al POS
    const activarFullscreenAutomatico = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
          setIsFullscreen(true);
          toast.success('Modo pantalla completa activado');
        }
      } catch (err) {
        // Los navegadores modernos requieren interacción del usuario antes de fullscreen
        // Mostrar prompt para que el usuario active fullscreen con un clic
        console.log('Fullscreen requiere interacción del usuario');
        setShowFullscreenPrompt(true);
      }
    };
    
    // Dar un pequeño delay para que la página cargue completamente
    setTimeout(activarFullscreenAutomatico, 500);
    
    // Focus on barcode input on mount
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }

    // Mantener el foco en el input de código de barras
    const keepFocus = () => {
      // Solo mantener foco si no hay un modal abierto y no hay otro input activo
      const activeElement = document.activeElement;
      const isInputActive = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' || 
        activeElement.tagName === 'SELECT'
      );
      
      // Si hay un input activo (como búsqueda), no forzar el foco al código de barras
      if (!paymentDialogOpen && !addPaymentDialogOpen && !saveOpenDialogOpen && !isInputActive && barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    };

    // Listener global para capturar escaneos de código de barras
    const handleGlobalKeyPress = (e) => {
      // Si hay un modal abierto, no interceptar
      if (paymentDialogOpen || addPaymentDialogOpen || saveOpenDialogOpen) {
        return;
      }

      // Si el foco está en cualquier input o textarea, permitir uso normal
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'SELECT')) {
        // Si está en el campo de búsqueda u otro input, no interceptar
        return;
      }

      // Si es una tecla alfanumérica, redirigir al input de código de barras
      if ((e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) || e.key === 'Enter') {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
          // Si no es Enter, agregar el carácter al input
          if (e.key !== 'Enter' && e.key.length === 1) {
            setBarcodeInput(prev => prev + e.key);
            e.preventDefault();
          }
        }
      }
    };

    // Listener para detectar cambios en fullscreen (por ejemplo, cuando se presiona ESC)
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Mantener foco cada 500ms
    const focusInterval = setInterval(keepFocus, 500);
    
    // Agregar listeners
    window.addEventListener('keypress', handleGlobalKeyPress);
    window.addEventListener('click', keepFocus);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      clearInterval(focusInterval);
      window.removeEventListener('keypress', handleGlobalKeyPress);
      window.removeEventListener('click', keepFocus);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [paymentDialogOpen, addPaymentDialogOpen, saveOpenDialogOpen]);

  useEffect(() => {
    filterProducts();
  }, [products, selectedCategory, searchTerm, ofertasActivas]);

  // Load CONSUMIDOR FINAL data when customers are loaded
  useEffect(() => {
    if (customers.length > 0 && !paymentData.cliente_nombre) {
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
      if (consumidorFinal) {
        setPaymentData({
          ...paymentData,
          cliente_nombre: consumidorFinal.nombre,
          cliente_rnc: consumidorFinal.rnc || '',
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
          tipo_ncf: consumidorFinal.tipo_ncf_predeterminado || 'B02',
        });
      }
    }
  }, [customers]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.filter(p => p.existencia > 0 && p.activo !== false));
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setCustomers(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Filter only active categories
      setCategories(response.data.filter(cat => cat.activa));
    } catch (error) {
      console.error('Error al cargar categorías');
    }
  };

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración');
    }
  };

  const fetchNCFTypes = async () => {
    try {
      const response = await axios.get(`${API}/ncf-types/sales`);
      setNcfTypes(response.data);
    } catch (error) {
      console.error('Error al cargar tipos NCF');
      // Fallback a tipo por defecto si falla
      setNcfTypes([{ code: 'B02', description: 'Consumidor Final' }]);
    }
  };


  const handleExitPOS = () => {
    // Confirmar si hay productos en el carrito
    if (cart.length > 0) {
      const confirm = window.confirm('Hay productos en el carrito. ¿Desea salir del POS?');
      if (!confirm) return;
    }
    
    // Navegar a la pantalla principal
    navigate('/');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Entrar en modo fullscreen
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        toast.error('No se pudo activar pantalla completa');
        console.error('Error al activar fullscreen:', err);
      });
    } else {
      // Salir de modo fullscreen
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        toast.error('No se pudo desactivar pantalla completa');
        console.error('Error al desactivar fullscreen:', err);
      });
    }
  };



  const fetchOpenInvoices = async () => {
    try {
      const response = await axios.get(`${API}/open-invoices`);
      setOpenInvoices(response.data);
    } catch (error) {
      console.error('Error al cargar facturas abiertas');
    }
  };

  const fetchOfertasActivas = async () => {
    try {
      const response = await axios.get(`${API}/ofertas?activas_solo=true`);
      setOfertasActivas(response.data);
    } catch (error) {
      console.error('Error al cargar ofertas activas');
    }
  };

  // Cargar créditos disponibles del cliente (Notas de Crédito)
  const fetchCreditosCliente = async (clienteId, clienteNombre) => {
    try {
      let creditosEncontrados = [];
      let totalDisponible = 0;

      // Primero buscar por ID si existe
      if (clienteId) {
        try {
          const responseById = await axios.get(`${API}/customer-credits/${clienteId}`);
          creditosEncontrados = responseById.data.creditos || [];
          totalDisponible = responseById.data.resumen?.saldo_disponible || 0;
        } catch (e) {
          console.log('Error buscando por ID, intentando por nombre');
        }
      }
      
      // Si no encontramos por ID, buscar por nombre
      // (para capturar créditos de facturas que no tenían cliente_id)
      if (creditosEncontrados.length === 0 && clienteNombre) {
        try {
          const responseByName = await axios.get(`${API}/customer-credits-by-name?nombre_cliente=${encodeURIComponent(clienteNombre)}`);
          creditosEncontrados = responseByName.data.creditos || [];
          totalDisponible = responseByName.data.total_disponible || 0;
        } catch (e) {
          console.log('Error buscando por nombre');
        }
      }
      
      // Si aún no hay créditos pero tenemos nombre, buscar con variaciones
      // (por si el nombre del cliente no coincide exactamente)
      if (creditosEncontrados.length === 0 && clienteNombre) {
        // Buscar solo con el primer nombre/palabra
        const primerNombre = clienteNombre.split(' ')[0];
        if (primerNombre.length >= 3) {
          try {
            const responsePartial = await axios.get(`${API}/customer-credits-by-name?nombre_cliente=${encodeURIComponent(primerNombre)}`);
            creditosEncontrados = responsePartial.data.creditos || [];
            totalDisponible = responsePartial.data.total_disponible || 0;
          } catch (e) {
            console.log('Error buscando por nombre parcial');
          }
        }
      }
      
      console.log(`Créditos encontrados para ${clienteNombre}: ${creditosEncontrados.length}, Total: ${totalDisponible}`);
      setCreditosDisponibles(creditosEncontrados);
      setTotalCreditoDisponible(totalDisponible);
    } catch (error) {
      console.error('Error al cargar créditos del cliente:', error);
      setCreditosDisponibles([]);
      setTotalCreditoDisponible(0);
    }
  };

  const handleSaveOpenInvoice = () => {
    if (cart.length === 0) {
      toast.error( 'El carrito está vacío');
      return;
    }
    
    // Si ya hay una factura cargada, actualizar directamente
    if (currentOpenInvoiceId) {
      updateOpenInvoice();
    } else {
      // Si no, abrir modal para nueva factura
      setSaveOpenDialogOpen(true);
    }
  };

  const confirmSaveOpenInvoice = async () => {
    if (!openInvoiceName.trim()) {
      toast.error( 'Ingrese un nombre para identificar la cuenta');
      return;
    }

    try {
      const totals = calculateTotals();
      const openInvoiceData = {
        nombre_identificador: openInvoiceName.trim(),
        items: cart.map(item => ({
          producto_id: item.producto_id,
          producto_nombre: item.nombre || item.producto_nombre,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario,
          subtotal: item.cantidad * item.precio_unitario
        })),
        subtotal: totals.subtotal,
        itbis: totals.itbis,
        total: totals.total
      };

      await axios.post(`${API}/open-invoices`, openInvoiceData);
      
      toast.success( `Cuenta "${openInvoiceName}" guardada exitosamente`);
      
      // Limpiar carrito y estado
      setCart([]);
      setOpenInvoiceName('');
      setSaveOpenDialogOpen(false);
      setCurrentOpenInvoiceId(null);
      
      // Recargar facturas abiertas
      fetchOpenInvoices();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error( errorMessage);
    }
  };

  const loadOpenInvoice = (invoice) => {
    // Cargar items al carrito con la estructura correcta
    setCart(invoice.items.map(item => ({
      producto_id: item.producto_id,
      nombre: item.producto_nombre, // El carrito usa 'nombre', no 'producto_nombre'
      cantidad: item.cantidad,
      precio_unitario: item.precio_unitario
    })));
    
    // Guardar ID y nombre para actualizar
    setCurrentOpenInvoiceId(invoice.id);
    setCurrentOpenInvoiceName(invoice.nombre_identificador);
    
    toast.success( `Cuenta "${invoice.nombre_identificador}" cargada`);
  };

  const updateOpenInvoice = async () => {
    if (cart.length === 0) {
      toast.error( 'El carrito está vacío');
      return;
    }

    if (!currentOpenInvoiceId) {
      toast.error( 'No hay factura abierta seleccionada');
      return;
    }

    try {
      const totals = calculateTotals();
      const updateData = {
        items: cart.map(item => ({
          producto_id: item.producto_id,
          producto_nombre: item.nombre || item.producto_nombre,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario,
          subtotal: item.cantidad * item.precio_unitario
        })),
        subtotal: totals.subtotal,
        itbis: totals.itbis,
        total: totals.total
      };

      await axios.patch(`${API}/open-invoices/${currentOpenInvoiceId}`, updateData);
      
      toast.success( `Cuenta "${currentOpenInvoiceName}" actualizada exitosamente`);
      
      // Limpiar carrito y estado
      setCart([]);
      setCurrentOpenInvoiceId(null);
      setCurrentOpenInvoiceName('');
      
      // Recargar facturas abiertas
      fetchOpenInvoices();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error( errorMessage);
    }
  };

  const deleteOpenInvoice = async (invoiceId, invoiceName) => {
    if (!window.confirm(`¿Eliminar la cuenta "${invoiceName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/open-invoices/${invoiceId}`);
      toast.success( 'Cuenta eliminada');
      fetchOpenInvoices();
      
      // Si era la factura actual, limpiar ID
      if (currentOpenInvoiceId === invoiceId) {
        setCurrentOpenInvoiceId(null);
      }
    } catch (error) {
      toast.error( 'Error al eliminar cuenta');
    }
  };


  const filterProducts = () => {
    let filtered = products;

    if (selectedCategory === 'ofertas') {
      // Filtrar solo productos con ofertas activas
      const productosConOfertasIds = ofertasActivas.map(o => o.producto_id).filter(id => id);
      filtered = filtered.filter(p => productosConOfertasIds.includes(p.id));
    } else if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.categoria === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codigo_interno.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const handleBarcodeScanner = async (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      try {
        const barcode = barcodeInput.trim();
        setBarcodeInput(''); // Limpiar inmediatamente para el próximo escaneo
        
        const response = await axios.get(`${API}/products/barcode/${barcode}`);
        addToCart(response.data);
        
        // Enfocar de nuevo el input
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      } catch (error) {
        toast.error( 'Producto no encontrado con este código de barras');
        
        // Enfocar de nuevo el input
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.producto_id === product.id);
    const esServicio = product.tipo_producto === 'SERVICIO';
    
    if (existingItem) {
      // No validar stock para servicios
      if (!esServicio && existingItem.cantidad >= product.existencia) {
        toast.error( 'No hay suficiente stock disponible');
        return;
      }
      const newCart = cart.map(item =>
        item.producto_id === product.id
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      );
      setCart(newCart);
      
      // Verificar y aplicar ofertas automáticamente
      if (businessConfig?.aplicar_ofertas_automaticamente) {
        setTimeout(() => verificarYAplicarOfertas(newCart), 100);
      }
    } else {
      const newCart = [
        ...cart,
        {
          producto_id: product.id,
          nombre: product.nombre,
          precio_unitario: product.precio_venta,
          precio_original: product.precio_venta,
          precio_minimo: product.precio_minimo || null, // Agregar precio mínimo
          precio_compra: product.precio_compra || 0,
          cantidad: 1,
          existencia: product.existencia,
          tipo_producto: product.tipo_producto,
          oferta_aplicada: null
        }
      ];
      setCart(newCart);
      
      // Verificar y aplicar ofertas automáticamente
      if (businessConfig?.aplicar_ofertas_automaticamente) {
        setTimeout(() => verificarYAplicarOfertas(newCart), 100);
      }
    }
    
    toast.success( `${product.nombre} agregado al carrito`);
  };
  
  // Función para aplicar el precio mínimo a un producto del carrito
  const aplicarPrecioMinimo = (productId) => {
    const item = cart.find(i => i.producto_id === productId);
    if (!item || !item.precio_minimo) {
      toast.error('Este producto no tiene precio mínimo definido');
      return;
    }
    
    if (item.precio_unitario === item.precio_minimo) {
      toast.info('Ya se está usando el precio mínimo');
      return;
    }
    
    const newCart = cart.map(i =>
      i.producto_id === productId
        ? { ...i, precio_unitario: i.precio_minimo, precio_minimo_aplicado: true }
        : i
    );
    setCart(newCart);
    toast.success(`Precio mínimo aplicado: ${formatCurrency(item.precio_minimo)}`);
  };
  
  // Función para restaurar el precio original
  const restaurarPrecioOriginal = (productId) => {
    const item = cart.find(i => i.producto_id === productId);
    if (!item) return;
    
    const newCart = cart.map(i =>
      i.producto_id === productId
        ? { ...i, precio_unitario: i.precio_original, precio_minimo_aplicado: false }
        : i
    );
    setCart(newCart);
    toast.success(`Precio restaurado: ${formatCurrency(item.precio_original)}`);
  };

  const updateQuantity = (productId, newQuantity) => {
    const item = cart.find(i => i.producto_id === productId);
    const esServicio = item.tipo_producto === 'SERVICIO';
    
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    // No validar stock para servicios
    if (!esServicio && newQuantity > item.existencia) {
      toast.error( 'No hay suficiente stock disponible');
      return;
    }

    const newCart = cart.map(item =>
      item.producto_id === productId
        ? { ...item, cantidad: newQuantity }
        : item
    );
    setCart(newCart);
    
    // Verificar y aplicar ofertas si está en automático
    if (businessConfig?.aplicar_ofertas_automaticamente) {
      setTimeout(() => verificarYAplicarOfertas(newCart), 100);
    }
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.producto_id !== productId);
    setCart(newCart);
    // Remover ofertas aplicadas relacionadas
    setOfertasAplicadas(ofertasAplicadas.filter(o => o.producto_id !== productId));
  };

  // Función principal para verificar y aplicar ofertas
  const verificarYAplicarOfertas = async (carritoActual) => {
    if (!ofertasActivas || ofertasActivas.length === 0) return;
    
    let cartModificado = [...carritoActual];
    const nuevasOfertasAplicadas = [];
    
    // Recorrer cada producto del carrito
    for (const item of cartModificado) {
      // Buscar ofertas aplicables al producto
      const ofertasProducto = ofertasActivas.filter(oferta => {
        // Verificar si la oferta aplica a este producto
        if (oferta.producto_id === item.producto_id) return true;
        // Ofertas de monto total se verifican después
        if (oferta.tipo === 'DESCUENTO_MONTO_TOTAL') return false;
        return false;
      });
      
      if (ofertasProducto.length === 0) continue;
      
      // Seleccionar oferta según prioridad
      let ofertaSeleccionada = null;
      if (businessConfig?.prioridad_ofertas_multiples === 'mejor_oferta') {
        // Calcular cuál es la mejor oferta (mayor descuento)
        ofertaSeleccionada = ofertasProducto.reduce((mejor, actual) => {
          const descuentoActual = calcularDescuentoOferta(actual, item);
          const descuentoMejor = mejor ? calcularDescuentoOferta(mejor, item) : 0;
          return descuentoActual > descuentoMejor ? actual : mejor;
        }, null);
      } else {
        // Primera encontrada
        ofertaSeleccionada = ofertasProducto[0];
      }
      
      if (ofertaSeleccionada) {
        const resultado = aplicarOferta(ofertaSeleccionada, item, cartModificado);
        if (resultado.aplicada) {
          cartModificado = resultado.carrito;
          nuevasOfertasAplicadas.push({
            oferta_id: ofertaSeleccionada.id,
            oferta_nombre: ofertaSeleccionada.nombre,
            oferta_tipo: ofertaSeleccionada.tipo,
            producto_id: item.producto_id,
            descuento: resultado.descuento || 0
          });
        }
      }
    }
    
    // Verificar ofertas de monto total
    const totalCarrito = cartModificado.reduce((sum, item) => sum + (item.precio_unitario * item.cantidad), 0);
    const ofertaMontoTotal = ofertasActivas.find(o => 
      o.tipo === 'DESCUENTO_MONTO_TOTAL' && 
      o.monto_minimo && 
      totalCarrito >= o.monto_minimo
    );
    
    if (ofertaMontoTotal) {
      nuevasOfertasAplicadas.push({
        oferta_id: ofertaMontoTotal.id,
        oferta_nombre: ofertaMontoTotal.nombre,
        oferta_tipo: 'DESCUENTO_MONTO_TOTAL',
        descuento: totalCarrito * (ofertaMontoTotal.porcentaje_descuento / 100)
      });
    }
    
    setCart(cartModificado);
    setOfertasAplicadas(nuevasOfertasAplicadas);
  };

  // Calcular descuento de una oferta para comparar
  const calcularDescuentoOferta = (oferta, item) => {
    switch (oferta.tipo) {
      case 'AL_COSTO':
        return item.precio_unitario - item.precio_compra;
      case 'PORCENTAJE_DESCUENTO':
        return item.precio_unitario * (oferta.porcentaje_descuento / 100);
      case 'DOS_POR_UNO':
        return item.cantidad >= 2 ? item.precio_unitario : 0;
      case 'SEGUNDA_MITAD_PRECIO':
        return item.cantidad >= 2 ? (item.precio_unitario * 0.5) : 0;
      case 'DESCUENTO_CANTIDAD':
        return (item.cantidad >= oferta.cantidad_minima) ? (item.precio_unitario * (oferta.porcentaje_descuento / 100)) : 0;
      default:
        return 0;
    }
  };

  // Aplicar una oferta específica a un producto
  const aplicarOferta = (oferta, item, carrito) => {
    let carritoModificado = [...carrito];
    let aplicada = false;
    let descuento = 0;
    
    const itemIndex = carritoModificado.findIndex(i => i.producto_id === item.producto_id);
    if (itemIndex === -1) return { carrito: carritoModificado, aplicada: false };
    
    switch (oferta.tipo) {
      case 'AL_COSTO':
        // Vender al precio de costo
        carritoModificado[itemIndex].precio_unitario = item.precio_compra;
        carritoModificado[itemIndex].oferta_aplicada = `Al costo`;
        descuento = (item.precio_original - item.precio_compra) * item.cantidad;
        aplicada = true;
        toast.info(`Oferta aplicada: ${oferta.nombre}`);
        break;
        
      case 'PORCENTAJE_DESCUENTO':
        // Aplicar descuento porcentual
        const precioConDesc = item.precio_original * (1 - oferta.porcentaje_descuento / 100);
        carritoModificado[itemIndex].precio_unitario = precioConDesc;
        carritoModificado[itemIndex].oferta_aplicada = `${oferta.porcentaje_descuento}% desc`;
        descuento = (item.precio_original - precioConDesc) * item.cantidad;
        aplicada = true;
        toast.info(`Oferta aplicada: ${oferta.nombre} (-${oferta.porcentaje_descuento}%)`);
        break;
        
      case 'DOS_POR_UNO':
        // 2x1: Si hay 2 o más, cobrar solo la mitad
        if (item.cantidad >= 2) {
          const cantidadAPagar = Math.floor(item.cantidad / 2) + (item.cantidad % 2);
          const precioEfectivo = (item.precio_original * cantidadAPagar) / item.cantidad;
          carritoModificado[itemIndex].precio_unitario = precioEfectivo;
          carritoModificado[itemIndex].oferta_aplicada = `2x1`;
          descuento = item.precio_original * (item.cantidad - cantidadAPagar);
          aplicada = true;
          toast.info(`Oferta aplicada: ${oferta.nombre} (2x1)`);
        }
        break;
        
      case 'SEGUNDA_MITAD_PRECIO':
        // Segunda unidad a mitad de precio
        if (item.cantidad >= 2) {
          const unidadesCompletas = Math.floor(item.cantidad / 2);
          const unidadesMitad = Math.floor(item.cantidad / 2);
          const unitariaExtra = item.cantidad % 2;
          const totalPrecio = (unidadesCompletas * item.precio_original) + 
                              (unidadesMitad * item.precio_original * 0.5) + 
                              (unitariaExtra * item.precio_original);
          carritoModificado[itemIndex].precio_unitario = totalPrecio / item.cantidad;
          carritoModificado[itemIndex].oferta_aplicada = `2da a 50%`;
          descuento = (item.precio_original * item.cantidad) - totalPrecio;
          aplicada = true;
          toast.info(`Oferta aplicada: ${oferta.nombre} (2da a mitad)`);
        }
        break;
        
      case 'COMPRA_LLEVA_GRATIS':
        // Compra X lleva Y gratis
        if (item.cantidad >= oferta.cantidad_requerida) {
          const productName = products.find(p => p.id === oferta.producto_gratis_id)?.nombre || 'Producto';
          // Agregar producto gratis al carrito
          const existeGratis = carritoModificado.find(i => i.producto_id === oferta.producto_gratis_id && i.es_gratis);
          if (!existeGratis) {
            const productoGratis = products.find(p => p.id === oferta.producto_gratis_id);
            if (productoGratis) {
              carritoModificado.push({
                producto_id: productoGratis.id,
                nombre: `${productoGratis.nombre} (GRATIS)`,
                precio_unitario: 0,
                precio_original: productoGratis.precio_venta,
                cantidad: oferta.cantidad_gratis || 1,
                existencia: productoGratis.existencia,
                es_gratis: true,
                oferta_aplicada: 'Gratis'
              });
              descuento = productoGratis.precio_venta * (oferta.cantidad_gratis || 1);
              aplicada = true;
              toast.info(`Oferta aplicada: ${productName} GRATIS`);
            }
          }
        }
        break;
        
      case 'DESCUENTO_CANTIDAD':
        // Descuento por cantidad
        if (item.cantidad >= oferta.cantidad_minima) {
          const precioConDesc = item.precio_original * (1 - oferta.porcentaje_descuento / 100);
          carritoModificado[itemIndex].precio_unitario = precioConDesc;
          carritoModificado[itemIndex].oferta_aplicada = `${oferta.porcentaje_descuento}% (${item.cantidad}+ uds)`;
          descuento = (item.precio_original - precioConDesc) * item.cantidad;
          aplicada = true;
          toast.info(`Oferta aplicada: ${oferta.nombre} (-${oferta.porcentaje_descuento}%)`);
        }
        break;
    }
    
    return { carrito: carritoModificado, aplicada, descuento };
  };

  // Función para aplicar ofertas manualmente
  const aplicarOfertasManualmente = () => {
    verificarYAplicarOfertas(cart);
  };

  // Función para obtener la oferta activa de un producto
  const getProductoOferta = (productoId) => {
    if (!ofertasActivas || ofertasActivas.length === 0) return null;
    
    const oferta = ofertasActivas.find(o => o.producto_id === productoId);
    if (!oferta) return null;
    
    // Formatear el texto de la oferta según el tipo
    switch (oferta.tipo) {
      case 'AL_COSTO':
        return { texto: 'AL COSTO', color: 'bg-blue-600', icon: '💰' };
      case 'PORCENTAJE_DESCUENTO':
        return { texto: `${oferta.porcentaje_descuento}% DESC`, color: 'bg-green-600', icon: '🔥' };
      case 'DOS_POR_UNO':
        return { texto: '2x1', color: 'bg-purple-600', icon: '🎁' };
      case 'SEGUNDA_MITAD_PRECIO':
        return { texto: '2da 50%', color: 'bg-orange-600', icon: '🎯' };
      case 'COMPRA_LLEVA_GRATIS':
        return { texto: `Compra ${oferta.cantidad_requerida} Lleva Gratis`, color: 'bg-pink-600', icon: '🎁' };
      case 'DESCUENTO_CANTIDAD':
        return { texto: `${oferta.cantidad_minima}+ = ${oferta.porcentaje_descuento}%`, color: 'bg-indigo-600', icon: '📦' };
      default:
        return { texto: 'OFERTA', color: 'bg-emerald-600', icon: '✨' };
    }
  };

  // Función para obtener productos con ofertas
  const getProductosConOfertas = () => {
    if (!ofertasActivas || ofertasActivas.length === 0) return [];
    return products.filter(p => ofertasActivas.some(o => o.producto_id === p.id));
  };

  const clearCart = () => {
    setCart([]);
    setOfertasAplicadas([]);
    setSelectedCustomer(null);
    setCurrentOpenInvoiceId(null);
    setCurrentOpenInvoiceName('');
    // Load CONSUMIDOR FINAL data automatically
    const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
    setPaymentData({
      tipo_ncf: 'B02',
      cliente_id: '',
      cliente_nombre: consumidorFinal?.nombre || 'CONSUMIDOR FINAL',
      cliente_rnc: consumidorFinal?.rnc || '000000000',
      cliente_telefono: consumidorFinal?.telefono || '000-000-0000',
      cliente_direccion: consumidorFinal?.direccion || 'SIN DIRECCION',
      tipo_pago: 'efectivo',
      es_credito: false,
      dias_credito: 30,
    });
  };

  const filterCustomers = () => {
    if (!customerSearchTerm.trim()) {
      return customers;
    }
    
    const searchLower = customerSearchTerm.toLowerCase().trim();
    return customers.filter(customer => 
      customer.nombre?.toLowerCase().includes(searchLower) ||
      customer.rnc?.toLowerCase().includes(searchLower) ||
      customer.cedula?.toLowerCase().includes(searchLower) ||
      customer.cedula_rnc?.toLowerCase().includes(searchLower) ||
      customer.telefono?.toLowerCase().includes(searchLower) ||
      customer.codigo_cliente?.toLowerCase().includes(searchLower)
    );
  };

  // Búsqueda rápida de cliente por identificador (auto-match)
  const searchCustomerByIdentifier = (searchValue) => {
    if (!searchValue) return null;
    const search = searchValue.trim().toLowerCase();
    return customers.find(c =>
      c.rnc?.toLowerCase() === search ||
      c.cedula?.toLowerCase() === search ||
      c.cedula_rnc?.toLowerCase() === search ||
      c.codigo_cliente?.toLowerCase() === search ||
      c.telefono === search
    );
  };

  const handleQuickCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    if (value.length >= 3) {
      const found = searchCustomerByIdentifier(value);
      if (found) {
        handleCustomerSelect(found.id);
        setCustomerSearchTerm('');
        toast.success(`Cliente: ${found.nombre}`);
      }
    }
  };

  const handleQuickCustomerSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setCustomerSearchOpen(true);
    }
  };

  const selectCustomerFromModal = (customer) => {
    handleCustomerSelect(customer.id);
    setCustomerSearchOpen(false);
    setCustomerSearchTerm('');
  };

  const clearSelectedCustomer = () => {
    handleCustomerSelect('new');
    setCustomerSearchTerm('');
    setRncPreview(null);
  };

  const handleCustomerSelect = (customerId) => {
    // Limpiar créditos aplicados cuando cambia el cliente
    setCreditosAplicados([]);
    
    if (customerId === "new") {
      // Load CONSUMIDOR FINAL data automatically
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
      setSelectedCustomer(null);
      if (consumidorFinal) {
        setPaymentData({
          ...paymentData,
          cliente_id: '',
          cliente_nombre: consumidorFinal.nombre,
          cliente_rnc: consumidorFinal.rnc || '',
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
          tipo_ncf: consumidorFinal.tipo_ncf_predeterminado || 'B02',
        });
        // Cargar notas de crédito del cliente CONSUMIDOR FINAL (CLI-00000001)
        fetchCreditosCliente(consumidorFinal.id, consumidorFinal.nombre);
      } else {
        // Fallback if CONSUMIDOR FINAL not found
        setPaymentData({
          ...paymentData,
          cliente_id: '',
          cliente_nombre: 'CONSUMIDOR FINAL',
          cliente_rnc: '000000000',
          cliente_telefono: '000-000-0000',
          cliente_direccion: 'SIN DIRECCION',
          tipo_ncf: 'B02',
        });
        // Buscar notas de crédito por nombre CONSUMIDOR FINAL
        fetchCreditosCliente(null, 'CONSUMIDOR FINAL');
      }
    } else {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setPaymentData({
          ...paymentData,
          cliente_id: customer.id,
          cliente_nombre: customer.nombre,
          cliente_rnc: customer.rnc || '',
          cliente_telefono: customer.telefono || '',
          cliente_direccion: customer.direccion || '',
          tipo_ncf: customer.tipo_ncf_predeterminado,
        });
        // Cargar créditos disponibles del cliente
        fetchCreditosCliente(customer.id, customer.nombre);
      }
    }
  };

  const calculateTotals = () => {
    // El precio de venta YA INCLUYE el ITBIS
    const totalConItbis = cart.reduce((sum, item) => sum + (item.precio_unitario * item.cantidad), 0);
    
    if (businessConfig?.itbis_habilitado) {
      // Calcular el subtotal SIN ITBIS (precio base)
      // Formula: subtotal = total / (1 + tasa_itbis)
      const tasaItbis = businessConfig.itbis_porcentaje / 100;
      const subtotal = totalConItbis / (1 + tasaItbis);
      const itbis = totalConItbis - subtotal;
      
      return { 
        subtotal: Math.round(subtotal * 100) / 100, 
        itbis: Math.round(itbis * 100) / 100, 
        total: totalConItbis 
      };
    } else {
      // Si ITBIS no está habilitado, el precio es el subtotal
      return { 
        subtotal: totalConItbis, 
        itbis: 0, 
        total: totalConItbis 
      };
    }
  };

  const getErrorMessage = (error) => {
    // Handle Pydantic validation errors (array of objects)
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(err => err.msg).join(', ');
    }
    
    // Handle simple string errors
    if (typeof error.response?.data?.detail === 'string') {
      return error.response.data.detail;
    }
    
    // Handle object errors
    if (typeof error.response?.data?.detail === 'object') {
      return JSON.stringify(error.response.data.detail);
    }
    
    // Default error message
    return 'Error al completar la venta';
  };

  const printInvoice = (invoice) => {
    // Funciones auxiliares para formatear fecha/hora en impresión
    const formatPrintDate = (dateStr) => {
      if (!dateStr) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
      }
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        return dateStr.split(' ')[0];
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        const now = new Date();
        return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
      }
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };
    
    const formatPrintTime = (dateStr) => {
      if (!dateStr) {
        const now = new Date();
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      }
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const timeOnly = parts[1].split(':').slice(0, 2).join(':');
          const ampmFormatted = parts[2].toLowerCase() === 'pm' ? 'p. m.' : 'a. m.';
          return `${timeOnly} ${ampmFormatted}`;
        }
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        const now = new Date();
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      }
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    // Usar valores de businessConfig o fallbacks
    const config = {
      logo: businessConfig?.logo || '',
      nombre_comercio: businessConfig?.nombre_comercio || businessConfig?.sistema_nombre || 'Nombre Comercial *',
      sistema_nombre: businessConfig?.sistema_nombre || 'Sistema de Gestión',
      rnc: businessConfig?.rnc || '',
      telefono: businessConfig?.telefono || '',
      direccion: businessConfig?.direccion || '',
      slogan: businessConfig?.slogan || ''
    };
    
    const printWindow = window.open('', '', 'width=280,height=600');
    
    // Optimized format for 72mm thermal printers - HIGH CLARITY
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Factura ${invoice.numero_factura || invoice.id}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: 72mm auto;
              margin: 2mm;
            }
            
            @media print {
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body { 
              font-family: 'Arial', 'Helvetica', sans-serif; 
              font-size: 12px;
              line-height: 1.4;
              width: 68mm;
              margin: 0 auto;
              padding: 3mm;
              color: #000;
              background: #fff;
            }
            
            .header { 
              text-align: center; 
              border-bottom: 2px dashed #000; 
              padding-bottom: 3mm; 
              margin-bottom: 3mm; 
            }
            
            .logo { 
              max-width: 45mm; 
              max-height: 18mm; 
              margin: 0 auto 2mm; 
              display: block; 
            }
            
            h2 { 
              margin: 2mm 0; 
              font-size: 16px; 
              font-weight: bold;
              letter-spacing: 0.5px;
            }
            
            p { 
              margin: 1mm 0; 
              font-size: 11px;
              line-height: 1.3;
            }
            
            .info { 
              margin-bottom: 3mm; 
              font-size: 11px;
              line-height: 1.4;
            }
            
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1mm 0;
              font-size: 11px;
              line-height: 1.4;
            }
            
            .separator { 
              border-top: 1px dashed #000; 
              margin: 3mm 0; 
            }
            
            .items { 
              margin: 3mm 0; 
            }
            
            .item-row { 
              margin: 2mm 0; 
            }
            
            .item-detail { 
              display: flex; 
              justify-content: space-between; 
              font-size: 11px;
              line-height: 1.4;
            }
            
            .totals { 
              margin-top: 3mm; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
            }
            
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1.5mm 0; 
              font-size: 12px;
              line-height: 1.4;
            }
            
            .grand-total { 
              font-weight: bold; 
              font-size: 15px; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
              margin-top: 2mm; 
            }
            
            .footer { 
              text-align: center; 
              margin-top: 4mm; 
              font-size: 10px; 
              border-top: 1px dashed #000; 
              padding-top: 3mm; 
              line-height: 1.4;
            }
            
            .bold { 
              font-weight: bold; 
            }

            .small { 
              font-size: 10px; 
            }

            .product-name {
              font-size: 11px;
              font-weight: bold;
              margin-bottom: 1mm;
              line-height: 1.3;
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
            <h2>${config.nombre_comercio || config.sistema_nombre}</h2>
            ${config.slogan ? `<p class="small">${config.slogan}</p>` : ''}
            ${config.rnc ? `<p>RNC: ${config.rnc}</p>` : ''}
            ${config.telefono ? `<p>Tel: ${config.telefono}</p>` : ''}
            ${config.direccion ? `<p class="small">${config.direccion}</p>` : ''}
          </div>
          
          <div class="info">
            <div class="info-row"><span>Factura:</span><span class="bold">${invoice.numero_factura || invoice.id?.slice(0,8)}</span></div>
            <div class="info-row"><span>NCF:</span><span class="bold">${invoice.ncf || 'N/A'}</span></div>
            <div class="info-row"><span>Tipo:</span><span>${invoice.tipo_ncf || 'B02'}</span></div>
            <div class="info-row"><span>Fecha:</span><span>${formatPrintDate(invoice.fecha)}</span></div>
            <div class="info-row"><span>Hora:</span><span>${formatPrintTime(invoice.fecha)}</span></div>
            <div class="info-row"><span>Cajero:</span><span>${invoice.cajero_nombre || invoice.usuario_nombre || user?.full_name || ''}</span></div>
          </div>

          ${invoice.cliente_nombre && invoice.cliente_nombre !== 'CONSUMIDOR FINAL' ? `
            <div class="separator"></div>
            <div class="info">
              <div class="bold" style="margin-bottom: 1mm; font-size: 12px;">CLIENTE:</div>
              <div style="font-size: 11px;">${invoice.cliente_nombre}</div>
              ${invoice.cliente_rnc && invoice.cliente_rnc !== '000000000' && invoice.cliente_rnc !== '000-0000000-0' ? 
                `<div class="small">RNC: ${invoice.cliente_rnc}</div>` : ''}
            </div>
          ` : ''}

          <div class="separator"></div>

          <div class="items">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 11px; margin-bottom: 2mm; border-bottom: 2px solid #000; padding-bottom: 1mm;">
              <span style="flex: 3;">Producto</span>
              <span style="flex: 1; text-align: center;">Cant</span>
              <span style="flex: 1.5; text-align: right;">Total</span>
            </div>
            ${(invoice.items || invoice.productos || []).map(item => {
              const precioOriginal = item.precio_original || (item.precio_unitario || item.precio);
              const precioFinal = item.precio_unitario || item.precio;
              const tieneDescuento = item.oferta_aplicada && precioOriginal > precioFinal;
              const descuentoPorItem = tieneDescuento ? (precioOriginal - precioFinal) * item.cantidad : 0;
              
              return `
              <div class="item-row">
                <div class="product-name">${item.producto_nombre || item.nombre}</div>
                ${tieneDescuento ? `
                  <div style="font-size: 10px; color: #000; text-decoration: line-through; margin-bottom: 0.5mm;">
                    Precio Normal: ${item.cantidad} x RD$ ${precioOriginal.toFixed(2)} = RD$ ${(precioOriginal * item.cantidad).toFixed(2)}
                  </div>
                ` : ''}
                <div class="item-detail">
                  <span style="flex: 2;">${item.cantidad} x RD$ ${precioFinal.toFixed(2)}</span>
                  <span style="flex: 1.5; text-align: right; font-weight: bold;">RD$ ${item.subtotal.toFixed(2)}</span>
                </div>
                ${item.oferta_aplicada ? `
                  <div style="font-size: 11px; font-weight: bold; color: #000; margin-top: 1mm; border: 2px solid #000; padding: 1.5mm;">
                    ** OFERTA: ${item.oferta_aplicada} **
                    ${descuentoPorItem > 0 ? `<br>** AHORRO: RD$ ${descuentoPorItem.toFixed(2)} **` : ''}
                  </div>
                ` : ''}
              </div>
              `;
            }).join('')}
          </div>

          <div class="totals">
            ${(() => {
              // Calcular total de descuentos
              const totalDescuentos = (invoice.items || invoice.productos || []).reduce((sum, item) => {
                const precioOriginal = item.precio_original || (item.precio_unitario || item.precio);
                const precioFinal = item.precio_unitario || item.precio;
                const tieneDescuento = item.oferta_aplicada && precioOriginal > precioFinal;
                return sum + (tieneDescuento ? (precioOriginal - precioFinal) * item.cantidad : 0);
              }, 0);
              
              return totalDescuentos > 0 ? `
                <div class="separator"></div>
                <div class="total-row" style="font-weight: bold; font-size: 13px; border: 2px solid #000; padding: 2mm; margin-bottom: 2mm;">
                  <span>** DESCUENTOS APLICADOS **</span>
                  <span>- RD$ ${totalDescuentos.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              ` : '';
            })()}
            <div class="total-row">
              <span>Subtotal:</span>
              <span class="bold">RD$ ${invoice.subtotal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            ${invoice.itbis > 0 ? `
              <div class="total-row">
                <span>ITBIS (18%):</span>
                <span class="bold">RD$ ${invoice.itbis.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>TOTAL:</span>
              <span>RD$ ${invoice.total.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>

          <div class="separator"></div>

          <div class="info">
            <div class="bold" style="margin-bottom: 2mm; font-size: 11px;">Tipo de Venta:</div>
            ${invoice.es_credito ? `
              <div class="info-row"><span>Crédito:</span><span>${invoice.dias_credito} días</span></div>
            ` : `
              <div class="info-row"><span>Contado</span></div>
            `}
            ${(invoice.formas_pago && invoice.formas_pago.length > 0) ? 
              invoice.formas_pago.map(fp => {
                const tipoPagoFormateado = fp.tipo_pago === 'credito_favor' ? 'Nota de Crédito' :
                                          fp.tipo_pago === 'efectivo' ? 'Efectivo' :
                                          fp.tipo_pago === 'tarjeta' ? 'Tarjeta' :
                                          fp.tipo_pago === 'transferencia' ? 'Transferencia' :
                                          fp.tipo_pago === 'credito' ? 'Crédito' :
                                          fp.tipo_pago.charAt(0).toUpperCase() + fp.tipo_pago.slice(1);
                return `<div class="info-row"><span>${tipoPagoFormateado}:</span><span>RD$ ${fp.monto.toFixed(2)}</span></div>`;
              }).join('') :
              `<div class="info-row"><span>Pago:</span><span class="bold">${
                invoice.tipo_pago === 'credito_favor' ? 'NOTA DE CRÉDITO' :
                invoice.tipo_pago === 'efectivo' ? 'EFECTIVO' :
                invoice.tipo_pago === 'tarjeta' ? 'TARJETA' :
                invoice.tipo_pago === 'transferencia' ? 'TRANSFERENCIA' :
                invoice.tipo_pago === 'credito' ? 'CRÉDITO' :
                (invoice.tipo_pago || 'efectivo').toUpperCase()
              }</span></div>`
            }
            ${invoice.tipo_pago === 'efectivo' && invoice.monto_recibido ? `
              <div class="info-row"><span>Recibido:</span><span>RD$ ${invoice.monto_recibido.toFixed(2)}</span></div>
              <div class="info-row"><span>Cambio:</span><span>RD$ ${(invoice.cambio || 0).toFixed(2)}</span></div>
            ` : ''}
          </div>

          <div class="footer">
            <p class="bold" style="font-size: 12px; margin-bottom: 2mm;">¡Gracias por su compra!</p>
            <p style="font-size: 11px;">${config.nombre_comercio || config.sistema_nombre}</p>
            ${config.mensaje_factura ? `<p style="font-size: 10px; margin-top: 2mm;">${config.mensaje_factura}</p>` : ''}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    toast.success('Imprimiendo factura...');
  };

  // Consultar RNC en API externa
  const handleRncLookup = async () => {
    const rnc = paymentData.cliente_rnc?.trim().replace(/-/g, '');
    if (!rnc || rnc.length < 9) {
      toast.error('Ingrese un RNC válido (mínimo 9 dígitos)');
      return;
    }
    setRncLookupLoading(true);
    setRncPreview(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/rnc/lookup`, {
        params: { rnc },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.found) {
        setRncPreview({ name: res.data.name, status: res.data.status });
      } else {
        toast.error('RNC no encontrado');
        setPaymentData(prev => ({ ...prev, cliente_nombre: 'CONSUMIDOR FINAL' }));
      }
    } catch {
      toast.error('Error al consultar RNC');
    } finally {
      setRncLookupLoading(false);
    }
  };

  const confirmRncName = () => {
    if (rncPreview) {
      const updates = { cliente_nombre: rncPreview.name };
      if (rncPreview.status === 'ACTIVO') {
        updates.tipo_ncf = 'B01';
      }
      setPaymentData(prev => ({ ...prev, ...updates }));
      setRncPreview(null);
      toast.success(rncPreview.status === 'ACTIVO' ? 'Cliente actualizado y NCF cambiado a B01' : 'Nombre del cliente actualizado');
    }
  };

  const rejectRncName = () => {
    setRncPreview(null);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error( 'El carrito está vacío');
      return;
    }
    // Reset payment states
    setFormasPago([]);
    setMontoRecibido('');
    setCambio(0);
    setCreditosAplicados([]);
    setRncPreview(null);
    
    // Cargar notas de crédito del cliente actual
    if (selectedCustomer) {
      fetchCreditosCliente(selectedCustomer.id, selectedCustomer.nombre);
    } else {
      // Si no hay cliente seleccionado, buscar para CONSUMIDOR FINAL
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
      if (consumidorFinal) {
        fetchCreditosCliente(consumidorFinal.id, consumidorFinal.nombre);
      } else {
        fetchCreditosCliente(null, 'CONSUMIDOR FINAL');
      }
    }
    
    setPaymentDialogOpen(true);
  };

  // Abrir modal para agregar forma de pago
  const abrirModalFormaPago = (tipo) => {
    const totalPendiente = totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0);
    setSelectedPaymentType(tipo);
    setPaymentAmount(totalPendiente.toFixed(2)); // Pre-llenar con monto pendiente
    setPaymentReference('');
    setAddPaymentDialogOpen(true);
  };

  // Agregar forma de pago
  const agregarFormaPago = () => {
    const monto = parseFloat(paymentAmount);
    
    if (!monto || monto <= 0) {
      toast.error( 'Ingrese un monto válido');
      return;
    }
    
    const totalPendiente = totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0);
    if (monto > totalPendiente + 0.01) {
      toast.error( `El monto no puede ser mayor a RD$ ${totalPendiente.toFixed(2)}`);
      return;
    }

    setFormasPago([...formasPago, { 
      tipo_pago: selectedPaymentType, 
      monto: monto, 
      referencia: paymentReference 
    }]);
    
    setAddPaymentDialogOpen(false);
    toast.success( `Pago de ${formatCurrency(monto)} agregado`);
  };

  // Eliminar forma de pago
  const eliminarFormaPago = (index) => {
    const formaPagoEliminada = formasPago[index];
    // Si eliminamos un pago a crédito, resetear el flag es_credito
    if (formaPagoEliminada?.tipo_pago === 'credito') {
      setPaymentData(prev => ({ ...prev, es_credito: false }));
    }
    setFormasPago(formasPago.filter((_, i) => i !== index));
  };

  // Agregar pago a crédito (para clientes con crédito disponible)
  const agregarPagoCredito = () => {
    // Validar que hay un cliente seleccionado con crédito
    if (!selectedCustomer) {
      toast.error('Debe seleccionar un cliente registrado para facturar a crédito');
      return;
    }
    
    if (!selectedCustomer.tiene_credito) {
      toast.error('Este cliente no tiene crédito habilitado');
      return;
    }
    
    const creditoDisponible = selectedCustomer.credito_disponible || 0;
    const totalPendiente = totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0);
    
    if (totalPendiente <= 0) {
      toast.error('No hay monto pendiente por pagar');
      return;
    }
    
    if (totalPendiente > creditoDisponible) {
      toast.error(`Crédito insuficiente. Disponible: ${formatCurrency(creditoDisponible)}, Pendiente: ${formatCurrency(totalPendiente)}`);
      return;
    }
    
    // Verificar que no haya otra forma de pago crédito ya agregada
    if (formasPago.some(fp => fp.tipo_pago === 'credito')) {
      toast.error('Ya existe un pago a crédito agregado');
      return;
    }
    
    // Agregar el pago a crédito
    setFormasPago([...formasPago, { 
      tipo_pago: 'credito', 
      monto: totalPendiente, 
      referencia: `Crédito ${selectedCustomer.dias_credito || 30} días` 
    }]);
    
    // Marcar la venta como crédito
    setPaymentData(prev => ({ 
      ...prev, 
      es_credito: true, 
      dias_credito: selectedCustomer.dias_credito || 30 
    }));
    
    toast.success(`Factura a crédito: ${formatCurrency(totalPendiente)} (${selectedCustomer.dias_credito || 30} días)`);
  };

  // Abrir diálogo para aplicar crédito a favor (de notas de crédito)
  const abrirDialogoCreditoFavor = () => {
    if (creditosDisponibles.length === 0) {
      toast.error('No hay notas de crédito disponibles');
      return;
    }
    setCreditoDialogOpen(true);
  };

  // Aplicar un crédito a favor específico
  const aplicarCreditoFavor = (credito) => {
    const totalPendiente = totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0) - creditosAplicados.reduce((sum, ca) => sum + ca.monto_aplicar, 0);
    
    if (totalPendiente <= 0) {
      toast.error('No hay monto pendiente por pagar');
      return;
    }
    
    // Verificar si este crédito ya fue aplicado
    if (creditosAplicados.some(ca => ca.credito_id === credito.id)) {
      toast.error('Esta nota de crédito ya fue aplicada');
      return;
    }
    
    // Calcular cuánto aplicar (el menor entre el saldo del crédito y el monto pendiente)
    const montoAplicar = Math.min(credito.saldo_disponible, totalPendiente);
    
    // Agregar a créditos aplicados
    setCreditosAplicados([...creditosAplicados, {
      credito_id: credito.id,
      monto_aplicar: montoAplicar,
      nota_credito_numero: credito.nota_credito_numero,
      factura_origen: credito.factura_numero
    }]);
    
    // También agregar como forma de pago para el cálculo visual
    setFormasPago([...formasPago, {
      tipo_pago: 'credito_favor',
      monto: montoAplicar,
      referencia: `NC: ${credito.nota_credito_numero}`
    }]);
    
    toast.success(`Nota de Crédito ${credito.nota_credito_numero} aplicada: ${formatCurrency(montoAplicar)}`);
    setCreditoDialogOpen(false);
  };

  // Eliminar un crédito a favor aplicado
  const eliminarCreditoAplicado = (creditoId) => {
    setCreditosAplicados(creditosAplicados.filter(ca => ca.credito_id !== creditoId));
    // También eliminar de formas de pago
    const creditoRemovido = creditosAplicados.find(ca => ca.credito_id === creditoId);
    if (creditoRemovido) {
      setFormasPago(formasPago.filter(fp => 
        !(fp.tipo_pago === 'credito_favor' && fp.referencia === `NC: ${creditoRemovido.nota_credito_numero}`)
      ));
    }
  };


  // Calcular cambio cuando se ingresa monto recibido
  const calcularCambio = (montoRecibidoStr) => {
    setMontoRecibido(montoRecibidoStr);
    const monto = parseFloat(montoRecibidoStr) || 0;
    const totalEfectivo = formasPago
      .filter(fp => fp.tipo_pago === 'efectivo')
      .reduce((sum, fp) => sum + fp.monto, 0);
    const cambioCalculado = monto - totalEfectivo;
    setCambio(cambioCalculado > 0 ? cambioCalculado : 0);
  };

  const handleCompleteSale = async () => {
    if (!paymentData.cliente_nombre.trim()) {
      toast.error( 'Ingrese el nombre del cliente');
      return;
    }

    // Validar formas de pago
    const totalPagos = formasPago.reduce((sum, fp) => sum + fp.monto, 0);
    if (Math.abs(totalPagos - totals.total) > 0.01) {
      toast.error( `El total de pagos (RD$ ${totalPagos.toFixed(2)}) debe ser igual al total (RD$ ${totals.total.toFixed(2)})`);
      return;
    }

    if (paymentData.es_credito && !paymentData.cliente_id) {
      toast.error( 'Para ventas a crédito debe seleccionar un cliente registrado');
      return;
    }

    try {
      // Determinar tipo de pago principal (para compatibilidad)
      let tipoPagoPrincipal = 'efectivo';
      // Filtrar formas de pago que no sean crédito a favor para determinar el tipo principal
      const formasPagoSinCreditoFavor = formasPago.filter(fp => fp.tipo_pago !== 'credito_favor');
      if (formasPagoSinCreditoFavor.length > 0) {
        tipoPagoPrincipal = formasPagoSinCreditoFavor[0].tipo_pago;
      } else if (formasPago.length > 0) {
        tipoPagoPrincipal = formasPago[0].tipo_pago;
      }

      const invoiceData = {
        tipo_ncf: paymentData.tipo_ncf,
        cliente: {
          nombre: paymentData.cliente_nombre,
          rnc: paymentData.cliente_rnc,
          telefono: paymentData.cliente_telefono,
          direccion: paymentData.cliente_direccion,
        },
        items: cart.map(item => ({
          producto_id: item.producto_id,
          producto_nombre: item.nombre,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario,
          precio_original: item.precio_original || item.precio_unitario,
          oferta_aplicada: item.oferta_aplicada || null,
          subtotal: item.precio_unitario * item.cantidad,
        })),
        tipo_pago: tipoPagoPrincipal,
        formas_pago: formasPago.filter(fp => fp.tipo_pago !== 'credito_favor'), // Excluir crédito a favor de formas_pago
        monto_recibido: parseFloat(montoRecibido) || 0,
        cambio: cambio,
        es_credito: paymentData.es_credito,
        dias_credito: paymentData.dias_credito,
        creditos_aplicados: creditosAplicados, // Enviar créditos a favor aplicados
      };

      if (paymentData.cliente_id) {
        invoiceData.cliente_id = paymentData.cliente_id;
      }

      const response = await axios.post(`${API}/invoices`, invoiceData);
      const createdInvoice = response.data;
      
      // Si venía de una factura abierta, eliminarla
      if (currentOpenInvoiceId) {
        try {
          await axios.delete(`${API}/open-invoices/${currentOpenInvoiceId}`);
          setCurrentOpenInvoiceId(null);
          fetchOpenInvoices();
        } catch (error) {
          console.error('Error al eliminar factura abierta:', error);
        }
      }
      
      toast.success( '¡Venta completada exitosamente!');
      
      setPaymentDialogOpen(false);
      clearCart();
      fetchProducts();
      fetchCustomers();
      // Limpiar créditos aplicados
      setCreditosAplicados([]);
      setCreditosDisponibles([]);
      setTotalCreditoDisponible(0);
      
      // Automatically print invoice
      setTimeout(() => {
        printInvoice(createdInvoice);
      }, 500);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error( errorMessage);
      console.error('Error completo:', error.response?.data);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart size={32} />
            <div>
              <h1 className="text-2xl font-bold">Punto de Venta</h1>
              <p className="text-sm text-emerald-100">
                {businessConfig?.nombre_comercio || 'LICORSTORE RD'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-emerald-100">Cajero</p>
              <p className="font-semibold">{user?.full_name}</p>
            </div>
            <Button
              onClick={toggleFullscreen}
              variant="outline"
              className="bg-blue-500 hover:bg-blue-600 text-white border-blue-600 h-12 px-4"
              title={isFullscreen ? "Salir de Pantalla Completa" : "Pantalla Completa"}
            >
              {isFullscreen ? (
                <>
                  <Minimize2 className="mr-2" size={20} />
                  Salir Completa
                </>
              ) : (
                <>
                  <Maximize className="mr-2" size={20} />
                  Pantalla Completa
                </>
              )}
            </Button>
            <Button
              onClick={handleExitPOS}
              variant="outline"
              className="bg-red-500 hover:bg-red-600 text-white border-red-600 h-12 px-4"
              title="Salir del POS"
            >
              <Home className="mr-2" size={20} />
              Salir
            </Button>
          </div>
        </div>
      </div>

      {/* Fullscreen Prompt Banner */}
      {showFullscreenPrompt && !isFullscreen && (
        <div className="bg-blue-600 text-white px-4 py-3 shadow-lg flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <Maximize size={24} />
            <div>
              <p className="font-semibold">¿Activar modo Pantalla Completa?</p>
              <p className="text-sm text-blue-100">Recomendado para mejor experiencia en el POS</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                try {
                  await document.documentElement.requestFullscreen();
                  setIsFullscreen(true);
                  setShowFullscreenPrompt(false);
                  toast.success('Modo pantalla completa activado');
                } catch (err) {
                  toast.error('No se pudo activar pantalla completa');
                }
              }}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              Sí, activar
            </Button>
            <Button
              onClick={() => setShowFullscreenPrompt(false)}
              variant="outline"
              className="border-white text-white hover:bg-blue-700"
            >
              No, gracias
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Side - Products */}
        <div className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Search and Barcode Scanner */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-2 h-5">
                  <Label className="text-xs font-semibold">Escanear Código de Barras</Label>
                  <span className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse"></span>
                </div>
                <div className="relative">
                  <Scan className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-500 animate-pulse" size={20} />
                  <Input
                    ref={barcodeInputRef}
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyPress={handleBarcodeScanner}
                    placeholder="Escanee o escriba el código..."
                    className="pl-10 h-12 text-lg border-2 border-emerald-500 focus:border-emerald-600 bg-emerald-50"
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center mb-2 h-5">
                  <Label className="text-xs font-semibold">Buscar Producto</Label>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre..."
                    className="pl-10 h-12 text-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="bg-white rounded-lg shadow p-3 mb-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                onClick={() => setSelectedCategory('all')}
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                className={selectedCategory === 'all' ? 'bg-emerald-600' : ''}
              >
                Todos
              </Button>
              {/* Botón Ofertas - solo si hay productos con ofertas */}
              {getProductosConOfertas().length > 0 && (
                <Button
                  onClick={() => setSelectedCategory('ofertas')}
                  variant={selectedCategory === 'ofertas' ? 'default' : 'outline'}
                  className={selectedCategory === 'ofertas' ? 'bg-red-600 hover:bg-red-700' : 'border-red-600 text-red-600 hover:bg-red-50'}
                >
                  <Tag className="mr-1" size={16} />
                  Ofertas ({getProductosConOfertas().length})
                </Button>
              )}
              {categories.map(category => (
                <Button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.nombre)}
                  variant={selectedCategory === category.nombre ? 'default' : 'outline'}
                  className={selectedCategory === category.nombre ? 'bg-emerald-600' : ''}
                >
                  {category.nombre}
                </Button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 bg-white rounded-lg shadow overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-4">
              {filteredProducts.map(product => {
                const oferta = getProductoOferta(product.id);
                return (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className={`bg-white border-2 rounded-lg p-3 hover:shadow-lg transition-all text-left relative ${
                      oferta ? 'border-red-400 hover:border-red-500' : 'border-slate-200 hover:border-emerald-500'
                    }`}
                  >
                    {/* Badge de oferta en la esquina superior derecha */}
                    {oferta && (
                      <div className={`absolute -top-2 -right-2 ${oferta.color} text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg z-10 flex items-center gap-1`}>
                        <span>{oferta.icon}</span>
                        <span>{oferta.texto}</span>
                      </div>
                    )}
                    
                    <div className="aspect-square bg-slate-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                      {product.imagen ? (
                        <img 
                          src={product.imagen} 
                          alt={product.nombre}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package size={40} className="text-slate-400" />
                      )}
                    </div>
                    <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.nombre}</h3>
                    <p className={`font-bold text-lg ${oferta ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrency(product.precio_venta)}
                    </p>
                    <p className="text-xs text-slate-500">Stock: {product.existencia}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Side - Cart */}
        <div className="w-96 bg-white shadow-2xl flex flex-col">
          <div className={`${currentOpenInvoiceId ? 'bg-amber-600' : 'bg-slate-800'} text-white p-4`}>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <ShoppingCart size={24} />
              Carrito ({cart.length})
            </h2>
            {currentOpenInvoiceId && (
              <p className="text-sm text-amber-100 mt-1">
                📝 Editando: {currentOpenInvoiceName}
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                <ShoppingCart size={64} className="mx-auto mb-4 opacity-50" />
                <p>El carrito está vacío</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item.producto_id} className={`rounded-lg p-3 ${item.es_gratis ? 'bg-green-50 border-2 border-green-300' : item.precio_minimo_aplicado ? 'bg-amber-50 border-2 border-amber-300' : 'bg-slate-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{item.nombre}</h4>
                        {item.oferta_aplicada && (
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full inline-block mt-1">
                            🎉 {item.oferta_aplicada}
                          </span>
                        )}
                        {item.precio_minimo_aplicado && (
                          <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full inline-block mt-1">
                            💰 Precio Mínimo
                          </span>
                        )}
                        {item.precio_original && item.precio_original !== item.precio_unitario && (
                          <span className="text-xs text-slate-500 line-through block mt-1">
                            {formatCurrency(item.precio_original)}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.producto_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    {/* Mostrar precio mínimo si existe y botón para aplicar */}
                    {item.precio_minimo && item.precio_minimo > 0 && !item.es_gratis && (
                      <div className="flex items-center justify-between mb-2 py-1 px-2 bg-amber-100 rounded border border-amber-200">
                        <span className="text-xs text-amber-700">
                          Mín: {formatCurrency(item.precio_minimo)}
                        </span>
                        {item.precio_unitario !== item.precio_minimo ? (
                          <button
                            onClick={() => aplicarPrecioMinimo(item.producto_id)}
                            className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded font-medium"
                          >
                            Usar Mínimo
                          </button>
                        ) : (
                          <button
                            onClick={() => restaurarPrecioOriginal(item.producto_id)}
                            className="text-xs bg-slate-500 hover:bg-slate-600 text-white px-2 py-1 rounded font-medium"
                          >
                            Restaurar
                          </button>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.producto_id, item.cantidad - 1)}
                          className="bg-slate-300 hover:bg-slate-400 w-8 h-8 rounded flex items-center justify-center"
                          disabled={item.es_gratis}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="font-bold w-8 text-center">{item.cantidad}</span>
                        <button
                          onClick={() => updateQuantity(item.producto_id, item.cantidad + 1)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white w-8 h-8 rounded flex items-center justify-center"
                          disabled={item.es_gratis}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      <span className={`font-bold ${item.es_gratis ? 'text-green-600' : item.precio_minimo_aplicado ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {formatCurrency(item.precio_unitario * item.cantidad)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals and Actions */}
          <div className="border-t p-4 space-y-3">
            {/* Información de ofertas aplicadas */}
            {ofertasAplicadas.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                <p className="text-xs font-semibold text-green-700 mb-1">🎉 Ofertas Aplicadas:</p>
                {ofertasAplicadas.map((oferta, i) => (
                  <p key={i} className="text-xs text-green-600">
                    • {oferta.oferta_nombre} {oferta.descuento > 0 && `(-${formatCurrency(oferta.descuento)})`}
                  </p>
                ))}
              </div>
            )}
            
            {/* Botón aplicar ofertas manualmente */}
            {!businessConfig?.aplicar_ofertas_automaticamente && cart.length > 0 && (
              <Button
                onClick={aplicarOfertasManualmente}
                variant="outline"
                className="w-full text-sm border-green-600 text-green-600 hover:bg-green-50"
              >
                <Tag className="mr-2" size={16} />
                Aplicar Ofertas
              </Button>
            )}
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {ofertasAplicadas.some(o => o.descuento > 0) && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuentos:</span>
                  <span>-{formatCurrency(ofertasAplicadas.reduce((sum, o) => sum + (o.descuento || 0), 0))}</span>
                </div>
              )}
              {businessConfig?.itbis_habilitado && (
                <div className="flex justify-between text-sm">
                  <span>ITBIS ({businessConfig.itbis_porcentaje}%):</span>
                  <span>{formatCurrency(totals.itbis)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span className="text-emerald-600">{formatCurrency(totals.total)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleCheckout}
                disabled={cart.length === 0}
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle className="mr-2" size={24} />
                Completar Venta
              </Button>
              {businessConfig?.allow_open_invoices && (
                <Button
                  onClick={handleSaveOpenInvoice}
                  disabled={cart.length === 0}
                  variant="outline"
                  className={`w-full ${currentOpenInvoiceId ? 'bg-amber-50 hover:bg-amber-100 border-amber-400' : 'bg-blue-50 hover:bg-blue-100 border-blue-300'}`}
                >
                  <Save className="mr-2" size={20} />
                  {currentOpenInvoiceId ? 'Actualizar Cuenta Abierta' : 'Guardar Cuenta Abierta'}
                </Button>
              )}
              <Button
                onClick={clearCart}
                disabled={cart.length === 0}
                variant="outline"
                className="w-full"
              >
                <X className="mr-2" size={20} />
                Limpiar Carrito
              </Button>
            </div>
          </div>
        </div>

        {/* Panel de Facturas Abiertas */}
        {businessConfig?.allow_open_invoices && (
          <div className={`${showOpenInvoices ? 'w-80' : 'w-12'} bg-white shadow-2xl transition-all duration-300 flex flex-col border-l-2 border-slate-200`}>
          <div className="bg-amber-600 text-white p-3 flex items-center justify-between">
            {showOpenInvoices ? (
              <>
                <h2 className="font-bold flex items-center gap-2">
                  <FileText size={20} />
                  Cuentas Abiertas ({openInvoices.length})
                </h2>
                <button onClick={() => setShowOpenInvoices(false)}>
                  <ChevronRight size={20} />
                </button>
              </>
            ) : (
              <button onClick={() => setShowOpenInvoices(true)} className="w-full">
                <ChevronLeft size={20} className="mx-auto" />
              </button>
            )}
          </div>

          {showOpenInvoices && (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {openInvoices.length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  <Clock size={48} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay cuentas abiertas</p>
                </div>
              ) : (
                openInvoices.map(invoice => {
                  const timeElapsed = Math.floor((new Date() - new Date(invoice.created_at)) / 60000); // minutos
                  return (
                    <div 
                      key={invoice.id} 
                      className={`border-2 rounded-lg p-3 ${currentOpenInvoiceId === invoice.id ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-sm">{invoice.nombre_identificador}</h3>
                        <button
                          onClick={() => deleteOpenInvoice(invoice.id, invoice.nombre_identificador)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-600 mb-1">{invoice.items.length} items</p>
                      <p className="font-bold text-emerald-600">{formatCurrency(invoice.total)}</p>
                      <p className="text-xs text-slate-500 mb-2">
                        <Clock size={12} className="inline mr-1" />
                        {timeElapsed < 60 ? `${timeElapsed} min` : `${Math.floor(timeElapsed/60)}h ${timeElapsed%60}min`}
                      </p>
                      <Button
                        onClick={() => loadOpenInvoice(invoice)}
                        size="sm"
                        variant="outline"
                        className="w-full text-xs"
                      >
                        Cargar
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          )}
          </div>
        )}
      </div>

      {/* Payment Dialog - Rediseño Swiss High-Contrast */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] overflow-hidden flex flex-col rounded-[32px] bg-slate-100 border border-slate-200 p-3 gap-0" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <DialogHeader className="sr-only">
            <DialogTitle>Completar Venta</DialogTitle>
            <DialogDescription>Formulario de pago</DialogDescription>
          </DialogHeader>
          
          {/* Grid principal de 3 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 flex-1 overflow-hidden h-full">
            
            {/* COLUMNA 1: Datos del Cliente */}
            <div className="lg:col-span-3 flex flex-col gap-5 bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm overflow-y-auto">
              <div>
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Paso 1</span>
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <User size={20} className="text-emerald-600" />
                  Cliente
                </h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 mb-1.5 block">Buscar Cliente (Código/RNC/Cédula/Tel/Nombre)</label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="customer-quick-search"
                      value={customerSearchTerm}
                      onChange={handleQuickCustomerSearch}
                      onKeyDown={handleQuickCustomerSearchKeyDown}
                      placeholder="Escriba código, nombre, RNC, cédula o teléfono..."
                      className="h-12 text-base rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 flex-1"
                      disabled={!!paymentData.cliente_id}
                    />
                    <button
                      type="button"
                      data-testid="customer-search-btn"
                      onClick={() => { setCustomerSearchTerm(''); setCustomerSearchOpen(true); }}
                      className="h-12 w-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition-colors shrink-0"
                    >
                      <Search size={18} />
                    </button>
                  </div>
                  {paymentData.cliente_id && (
                    <div className="mt-2 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-emerald-800 truncate">{paymentData.cliente_nombre}</p>
                        <p className="text-[10px] text-emerald-600">{paymentData.cliente_rnc || 'Sin RNC'}</p>
                      </div>
                      <button type="button" data-testid="clear-customer-btn" onClick={clearSelectedCustomer} className="w-7 h-7 rounded-lg flex items-center justify-center text-emerald-600 hover:text-rose-500 hover:bg-rose-50 transition-colors shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 mb-1.5 block">Cliente *</label>
                  <Input
                    data-testid="customer-name-input"
                    value={paymentData.cliente_nombre}
                    onChange={(e) => setPaymentData({ ...paymentData, cliente_nombre: e.target.value })}
                    placeholder="Nombre del cliente"
                    disabled={!!paymentData.cliente_id}
                    className="h-12 text-base rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 mb-1.5 block">RNC</label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="rnc-input"
                      value={paymentData.cliente_rnc}
                      onChange={(e) => { setPaymentData({ ...paymentData, cliente_rnc: e.target.value }); setRncPreview(null); }}
                      placeholder="RNC (opcional)"
                      disabled={!!paymentData.cliente_id}
                      className="h-12 text-base rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-emerald-500 flex-1"
                      onKeyDown={(e) => { if (e.key === 'Enter' && !paymentData.cliente_id) { e.preventDefault(); handleRncLookup(); } }}
                    />
                    <button
                      type="button"
                      data-testid="rnc-lookup-btn"
                      onClick={handleRncLookup}
                      disabled={!!paymentData.cliente_id || rncLookupLoading || !paymentData.cliente_rnc?.trim()}
                      className="h-12 w-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center transition-colors shrink-0"
                    >
                      {rncLookupLoading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                    </button>
                  </div>
                  {/* Vista previa del RNC encontrado */}
                  {rncPreview && (
                    <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 animate-in fade-in duration-200">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] tracking-[0.1em] uppercase font-bold text-emerald-600 mb-0.5">RNC Encontrado</p>
                          <p className="text-sm font-bold text-slate-900 leading-tight">{rncPreview.name}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Estado: {rncPreview.status}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          data-testid="rnc-confirm-btn"
                          onClick={confirmRncName}
                          className="flex-1 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors"
                        >
                          Confirmar
                        </button>
                        <button
                          type="button"
                          data-testid="rnc-reject-btn"
                          onClick={rejectRncName}
                          className="flex-1 h-8 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-bold transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 mb-1.5 block">Tipo NCF</label>
                  <Select 
                    data-testid="ncf-select"
                    value={paymentData.tipo_ncf} 
                    onValueChange={(value) => setPaymentData({ ...paymentData, tipo_ncf: value })}
                    disabled={!businessConfig?.ncf_habilitado || !!paymentData.cliente_id}
                  >
                    <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-emerald-500">
                      <SelectValue placeholder="Seleccionar tipo NCF" />
                    </SelectTrigger>
                    <SelectContent>
                      {ncfTypes.length > 0 ? (
                        ncfTypes.map((ncfType) => (
                          <SelectItem key={ncfType.code} value={ncfType.code}>
                            {ncfType.code} - {ncfType.description}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {!businessConfig?.ncf_habilitado && (
                    <p className="text-[10px] text-slate-400 mt-1">NCF deshabilitado en configuración</p>
                  )}
                  {ncfTypes.length === 0 && businessConfig?.ncf_habilitado && (
                    <p className="text-[10px] text-amber-500 mt-1">No hay tipos NCF registrados</p>
                  )}
                </div>
              </div>
            </div>

            {/* COLUMNA 2: Formas de Pago */}
            <div className="lg:col-span-5 flex flex-col gap-5 bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm overflow-y-auto">
              <div>
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Paso 2</span>
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <CreditCard size={20} className="text-emerald-600" />
                  Formas de Pago
                </h3>
              </div>

              {/* Grid de botones de pago */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  data-testid="payment-method-efectivo"
                  onClick={() => abrirModalFormaPago('efectivo')}
                  className="h-24 rounded-2xl border-2 border-slate-100 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-300 transition-all flex flex-col items-center justify-center gap-2 text-slate-700 font-semibold active:scale-[0.97]"
                >
                  <Banknote size={30} className="text-emerald-600" />
                  <span className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Efectivo</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-method-tarjeta"
                  onClick={() => abrirModalFormaPago('tarjeta')}
                  className="h-24 rounded-2xl border-2 border-slate-100 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 transition-all flex flex-col items-center justify-center gap-2 text-slate-700 font-semibold active:scale-[0.97]"
                >
                  <CreditCard size={30} className="text-blue-600" />
                  <span className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Tarjeta</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-method-transferencia"
                  onClick={() => abrirModalFormaPago('transferencia')}
                  className="h-24 rounded-2xl border-2 border-slate-100 bg-slate-50 hover:bg-purple-50 hover:border-purple-300 transition-all flex flex-col items-center justify-center gap-2 text-slate-700 font-semibold active:scale-[0.97]"
                >
                  <ArrowRightLeft size={30} className="text-purple-600" />
                  <span className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Transferencia</span>
                </button>
                
                {totalCreditoDisponible > 0 && (
                  <button
                    type="button"
                    data-testid="payment-method-nota-credito"
                    onClick={abrirDialogoCreditoFavor}
                    className="h-24 rounded-2xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 transition-all flex flex-col items-center justify-center gap-2 text-amber-800 font-semibold active:scale-[0.97]"
                  >
                    <Receipt size={30} className="text-amber-600" />
                    <span className="text-xs font-bold text-center leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>N. Crédito<br/><span className="text-[10px] font-semibold">{formatCurrency(totalCreditoDisponible)}</span></span>
                  </button>
                )}
                
                {selectedCustomer?.tiene_credito && selectedCustomer?.credito_disponible > 0 && (
                  <button
                    type="button"
                    data-testid="payment-method-credito"
                    onClick={agregarPagoCredito}
                    disabled={formasPago.some(fp => fp.tipo_pago === 'credito')}
                    className="h-24 rounded-2xl border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-400 transition-all flex flex-col items-center justify-center gap-2 text-orange-800 font-semibold disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]"
                  >
                    <Wallet size={30} className="text-orange-600" />
                    <span className="text-xs font-bold text-center leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>A Crédito<br/><span className="text-[10px] font-semibold">{formatCurrency(selectedCustomer.credito_disponible)}</span></span>
                  </button>
                )}
              </div>

              {/* Lista de pagos agregados */}
              {formasPago.length > 0 && (
                <div className="space-y-2">
                  <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500">Pagos Agregados</label>
                  <div className="space-y-2">
                    {formasPago.map((fp, index) => (
                      <div key={index} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            fp.tipo_pago === 'efectivo' ? 'bg-emerald-100' :
                            fp.tipo_pago === 'tarjeta' ? 'bg-blue-100' :
                            fp.tipo_pago === 'transferencia' ? 'bg-purple-100' :
                            fp.tipo_pago === 'credito' ? 'bg-orange-100' :
                            'bg-amber-100'
                          }`}>
                            {fp.tipo_pago === 'efectivo' && <Banknote size={20} className="text-emerald-600" />}
                            {fp.tipo_pago === 'tarjeta' && <CreditCard size={20} className="text-blue-600" />}
                            {fp.tipo_pago === 'transferencia' && <ArrowRightLeft size={20} className="text-purple-600" />}
                            {fp.tipo_pago === 'credito' && <Wallet size={20} className="text-orange-600" />}
                            {fp.tipo_pago === 'credito_favor' && <Receipt size={20} className="text-amber-600" />}
                          </div>
                          <div>
                            <div className="font-semibold capitalize text-sm text-slate-800">
                              {fp.tipo_pago === 'credito' ? 'Factura a Crédito' : 
                               fp.tipo_pago === 'credito_favor' ? 'Nota de Crédito' : 
                               fp.tipo_pago}
                            </div>
                            {(fp.tipo_pago === 'credito' || fp.tipo_pago === 'credito_favor') && fp.referencia && (
                              <div className="text-[10px] text-slate-500">{fp.referencia}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{formatCurrency(fp.monto)}</span>
                          <button
                            data-testid="remove-payment-btn"
                            onClick={() => {
                              if (fp.tipo_pago === 'credito_favor') {
                                const creditoAplicado = creditosAplicados.find(ca => 
                                  fp.referencia === `NC: ${ca.nota_credito_numero}`
                                );
                                if (creditoAplicado) {
                                  eliminarCreditoAplicado(creditoAplicado.credito_id);
                                }
                              } else {
                                eliminarFormaPago(index);
                              }
                            }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* COLUMNA 3: Resumen (Dark Command Center) */}
            <div className="lg:col-span-4 flex flex-col justify-between bg-slate-900 text-white p-8 rounded-[24px] shadow-xl border border-slate-800 overflow-y-auto">
              
              {/* Total principal */}
              <div className="space-y-6">
                <div>
                  <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Total a Pagar</span>
                  <div data-testid="total-pay-display" className="text-5xl font-black tracking-tighter text-white mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {formatCurrency(totals.total)}
                  </div>
                </div>

                {/* Desglose */}
                <div className="space-y-3 border-t border-slate-700 pt-5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400 font-medium">Total pagado</span>
                    <span data-testid="total-paid-display" className="text-xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {formatCurrency(formasPago.reduce((sum, fp) => sum + fp.monto, 0))}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400 font-medium">Pendiente</span>
                    <span data-testid="pending-amount-display" className={`text-2xl font-black ${
                      formasPago.reduce((sum, fp) => sum + fp.monto, 0) >= totals.total ? 'text-emerald-400' : 'text-rose-400'
                    }`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {formatCurrency(Math.max(0, totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0)))}
                    </span>
                  </div>
                </div>

                {/* Calculadora de cambio (solo si hay efectivo) */}
                {formasPago.some(fp => fp.tipo_pago === 'efectivo') && (
                  <div className="space-y-3 border-t border-slate-700 pt-5">
                    <label className="text-[10px] tracking-[0.12em] uppercase font-bold text-slate-500 flex items-center gap-2">
                      <Banknote size={14} className="text-emerald-400" />
                      Efectivo Recibido
                    </label>
                    <Input
                      data-testid="cash-received-input"
                      type="number"
                      value={montoRecibido}
                      onChange={(e) => calcularCambio(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      className="h-16 text-3xl font-bold bg-slate-800 border-slate-700 text-white text-right rounded-xl focus-visible:ring-emerald-500 placeholder:text-slate-600"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    />
                    {/* Botones de denominaciones RD$ */}
                    <div className="grid grid-cols-3 gap-2">
                      {[50, 100, 200, 500, 1000, 2000].map((denom) => (
                        <button
                          key={denom}
                          type="button"
                          data-testid={`denom-btn-${denom}`}
                          onClick={() => {
                            const current = parseFloat(montoRecibido) || 0;
                            const newVal = (current + denom).toString();
                            calcularCambio(newVal);
                          }}
                          className="h-11 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 hover:bg-emerald-900/40 hover:border-emerald-600 hover:text-emerald-300 transition-all text-sm font-bold active:scale-[0.95]"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          +{denom >= 1000 ? `${denom/1000}K` : denom}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      data-testid="denom-btn-exact"
                      onClick={() => calcularCambio(totals.total.toString())}
                      className="w-full h-9 rounded-lg bg-emerald-800/30 border border-emerald-700/50 text-emerald-400 hover:bg-emerald-800/50 transition-all text-xs font-bold active:scale-[0.98]"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Monto Exacto
                    </button>
                    {cambio > 0 && (
                      <div className="flex justify-between items-center bg-slate-800/50 rounded-xl p-4">
                        <span className="text-sm text-slate-400">Cambio</span>
                        <span data-testid="change-amount-display" className="text-4xl font-black text-emerald-400 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {formatCurrency(cambio)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div className="space-y-3 mt-6">
                <button
                  data-testid="confirm-sale-btn"
                  onClick={handleCompleteSale}
                  disabled={Math.abs(formasPago.reduce((sum, fp) => sum + fp.monto, 0) - totals.total) > 0.01}
                  className="h-20 w-full rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white text-2xl font-bold shadow-[0_8px_32px_rgba(16,185,129,0.25)] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <CheckCircle size={28} />
                  Confirmar Venta
                </button>
                <button
                  data-testid="cancel-sale-btn"
                  onClick={() => setPaymentDialogOpen(false)}
                  className="h-12 w-full rounded-xl bg-transparent border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-all text-sm font-medium"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Agregar Forma de Pago */}
      <Dialog open={addPaymentDialogOpen} onOpenChange={setAddPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {selectedPaymentType === 'efectivo' && (
                <>
                  <DollarSign className="text-green-600" size={24} />
                  <span>Pago en Efectivo</span>
                </>
              )}
              {selectedPaymentType === 'tarjeta' && (
                <>
                  <CreditCard className="text-blue-600" size={24} />
                  <span>Pago con Tarjeta</span>
                </>
              )}
              {selectedPaymentType === 'transferencia' && (
                <>
                  <span className="text-purple-600 text-2xl">↔️</span>
                  <span>Pago por Transferencia</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Monto pendiente: {formatCurrency(totals.total - formasPago.reduce((sum, fp) => sum + fp.monto, 0))}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-base font-semibold">Monto a pagar *</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="text-2xl font-bold h-14 text-center"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    agregarFormaPago();
                  }
                }}
              />
              <p className="text-xs text-slate-500 mt-1">
                Por defecto se muestra el monto pendiente total
              </p>
            </div>

            {selectedPaymentType === 'tarjeta' && (
              <div>
                <Label>Referencia / Últimos 4 dígitos (Opcional)</Label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Ej: **** 1234"
                  maxLength={20}
                />
              </div>
            )}

            {selectedPaymentType === 'transferencia' && (
              <div>
                <Label>Referencia / No. de Transacción (Opcional)</Label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Ej: TRF-123456"
                  maxLength={50}
                />
              </div>
            )}

            <div className="bg-slate-100 p-3 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Vista previa:</div>
              <div className="flex items-center justify-between">
                <span className="font-semibold capitalize">{selectedPaymentType}</span>
                <span className="text-lg font-bold text-emerald-600">
                  {formatCurrency(parseFloat(paymentAmount) || 0)}
                </span>
              </div>
              {paymentReference && (
                <div className="text-xs text-slate-500 mt-1">
                  Ref: {paymentReference}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setAddPaymentDialogOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={agregarFormaPago}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              Agregar
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog para Guardar Cuenta Abierta */}
      <Dialog open={saveOpenDialogOpen} onOpenChange={setSaveOpenDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Guardar Cuenta Abierta</DialogTitle>
            <DialogDescription>
              Ingrese un nombre para identificar esta cuenta (ej: Mesa 3, Juan, Cliente 5)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="open-invoice-name">Nombre Identificador *</Label>
              <Input
                id="open-invoice-name"
                value={openInvoiceName}
                onChange={(e) => setOpenInvoiceName(e.target.value)}
                placeholder="Ej: Mesa 5, Juan, Cliente 3..."
                className="text-lg"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    confirmSaveOpenInvoice();
                  }
                }}
              />
            </div>

            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">Resumen de la cuenta:</p>
              <p className="text-sm">{cart.length} productos</p>
              <p className="text-lg font-bold text-emerald-600">{formatCurrency(calculateTotals().total)}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setSaveOpenDialogOpen(false);
                setOpenInvoiceName('');
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmSaveOpenInvoice}
              disabled={!openInvoiceName.trim()}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              <Save className="mr-2" size={20} />
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Seleccionar Nota de Crédito */}
      <Dialog open={creditoDialogOpen} onOpenChange={setCreditoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Tag className="text-amber-600" />
              Notas de Crédito Disponibles
            </DialogTitle>
            <DialogDescription>
              Seleccione una nota de crédito para aplicar a esta venta
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {creditosDisponibles.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No hay notas de crédito disponibles para este cliente
              </div>
            ) : (
              creditosDisponibles.map((credito) => {
                const yaAplicado = creditosAplicados.some(ca => ca.credito_id === credito.id);
                
                return (
                  <div 
                    key={credito.id} 
                    className={`border-2 rounded-lg p-4 ${
                      yaAplicado 
                        ? 'bg-slate-100 border-slate-300 opacity-60' 
                        : 'bg-amber-50 border-amber-300 hover:border-amber-400 cursor-pointer'
                    }`}
                    onClick={() => !yaAplicado && aplicarCreditoFavor(credito)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-bold text-amber-800">
                          Nota de Crédito: {credito.nota_credito_numero}
                        </div>
                        <div className="text-sm text-slate-600">
                          Factura origen: {credito.factura_numero}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg text-amber-700">
                          {formatCurrency(credito.saldo_disponible)}
                        </div>
                        <div className="text-xs text-slate-500">Disponible</div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between text-sm text-slate-600 border-t pt-2 mt-2">
                      <span>Monto original: {formatCurrency(credito.monto_original)}</span>
                      <span>Usado: {formatCurrency(credito.monto_usado)}</span>
                    </div>
                    
                    {credito.fecha_creacion && (
                      <div className="text-xs text-slate-500 mt-1">
                        Creado: {credito.fecha_creacion}
                      </div>
                    )}
                    
                    {yaAplicado && (
                      <div className="mt-2 text-center text-sm text-green-600 font-semibold">
                        ✓ Ya aplicado a esta venta
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          {creditosDisponibles.length > 0 && (
            <div className="bg-amber-100 rounded-lg p-3 mt-4">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-amber-800">Total Disponible:</span>
                <span className="font-bold text-xl text-amber-700">
                  {formatCurrency(totalCreditoDisponible)}
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setCreditoDialogOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda Avanzada de Clientes */}
      <Dialog open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Clientes</DialogTitle>
            <DialogDescription>Busque por código, nombre, RNC, cédula o teléfono</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                data-testid="customer-modal-search"
                placeholder="Buscar por código, nombre, RNC, cédula o teléfono..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="pl-10 h-12 text-base"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Código</th>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-left p-2">RNC/Cédula</th>
                    <th className="text-left p-2">Teléfono</th>
                    <th className="text-right p-2">Crédito Disp.</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filterCustomers().map(customer => (
                    <tr key={customer.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => selectCustomerFromModal(customer)}>
                      <td className="p-2 font-mono text-slate-600 text-xs">{customer.codigo_cliente || 'N/A'}</td>
                      <td className="p-2 font-medium">{customer.nombre}</td>
                      <td className="p-2 text-slate-600">{customer.cedula_rnc || customer.rnc || customer.cedula || '-'}</td>
                      <td className="p-2 text-slate-600">{customer.telefono || '-'}</td>
                      <td className="p-2 text-right">
                        {customer.tiene_credito ? (
                          <span className="text-green-600 font-semibold">{formatCurrency(customer.credito_disponible || 0)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        <Button size="sm" onClick={() => selectCustomerFromModal(customer)} className="bg-emerald-500 hover:bg-emerald-600 text-xs">
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filterCustomers().length === 0 && (
                <div className="text-center py-8 text-slate-500">No se encontraron clientes</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Placeholder icon for products
const Package = ({ size, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

export default POS;
